/**
 * QuickAddPanel — right-hand "quick add" surface for the redesigned billing
 * screen. Replaces the old cart on the right with one-tap access to the
 * products a counter reaches for most:
 *
 *   • Popular — most frequently sold across recent invoices.
 *   • Recent  — most recently sold, newest first.
 *
 * Falls back to the store's active catalogue (A–Z) for a brand-new store with
 * no sales history, so the panel is never empty. Clicking a tile adds one unit
 * to the cart via `onAdd`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus, Sparkles, History, LayoutGrid } from 'lucide-react';
import { database } from '@/db';
import { Product, Category } from '@/db/models/Inventory';
import { Invoice, LineItem } from '@/db/models/Billing';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { useLiveStock } from '@/sync/liveStock';
import { cn } from '@/utils/cn';
import { Money } from '@/components/ui/numeric';

type Tab = 'popular' | 'recent' | 'browse';

/** Cap the "All categories" browse list so a huge catalogue stays snappy without
 *  virtualization — the category filter (or the search box) narrows past this. */
const BROWSE_CAP = 300;

interface Props {
  onAdd: (product: Product, qty?: number) => void;
}

export function QuickAddPanel({ onAdd }: Props) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [tab, setTab] = useState<Tab>('popular');
  const [browseCategory, setBrowseCategory] = useState<string | null>(null);
  const { popular, recent, loading } = useQuickAddProducts(activeStoreId);

  // ── Browse: categories for the filter + the (filtered) active catalogue ──
  const categories = useObservable<Category[]>(
    () => database.get<Category>('categories').query(Q.where('is_deleted', false)).observe(),
    [],
    [],
  );
  const categoryGroups = useMemo(() => {
    const byName = new Map<string, string[]>();
    for (const c of categories) {
      const name = (c.name || '').trim();
      if (!name) continue;
      if (!byName.has(name)) byName.set(name, []);
      byName.get(name)!.push(c.id);
    }
    return Array.from(byName.entries())
      .map(([name, ids]) => ({ name, ids }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  const browseProducts = useObservable<Product[]>(
    () => {
      if (!activeStoreId || tab !== 'browse') return null;
      const conds: Q.Clause[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        Q.where('is_active', true),
      ];
      if (browseCategory) {
        const grp = categoryGroups.find((g) => g.name === browseCategory);
        if (grp && grp.ids.length) conds.push(Q.where('category_id', Q.oneOf(grp.ids)));
      }
      return database
        .get<Product>('products')
        .query(...conds, Q.sortBy('name', Q.asc))
        .observeWithColumns(['current_stock', 'selling_price', 'name']);
    },
    [activeStoreId, tab, browseCategory, categoryGroups],
    [],
  );

  const list = tab === 'popular' ? popular : tab === 'recent' ? recent : browseProducts;
  const capped = tab === 'browse' ? browseProducts.slice(0, BROWSE_CAP) : list;

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-stroke-hairline/10 bg-bg-surface">
      <div className="border-b border-stroke-hairline/10 px-4 py-3">
        <div className="text-heading text-fg-primary">Quick add</div>
        {/* Sentence case. Small caps are for column headers and field labels —
            one- or two-word tokens the eye indexes on. This is a sentence of
            instruction, and setting it in caps makes it slower to read, not
            more important. */}
        <div className="text-label text-fg-tertiary">Tap to add to cart</div>
      </div>

      {/* Tabs */}
      <div className="flex shrink-0 gap-1 border-b border-stroke-hairline/10 p-1.5">
        <TabButton active={tab === 'popular'} onClick={() => setTab('popular')} icon={<Sparkles size={14} />} label="Popular" />
        <TabButton active={tab === 'recent'} onClick={() => setTab('recent')} icon={<History size={14} />} label="Recent" />
        <TabButton active={tab === 'browse'} onClick={() => setTab('browse')} icon={<LayoutGrid size={14} />} label="Browse" />
      </div>

      {/* Browse category filter */}
      {tab === 'browse' && (
        <div className="shrink-0 border-b border-stroke-hairline/10 p-1.5">
          <select
            aria-label="Filter by category"
            value={browseCategory ?? ''}
            onChange={(e) => setBrowseCategory(e.target.value || null)}
            className="h-8 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-2 text-body text-fg-primary"
          >
            <option value="">All categories</option>
            {categoryGroups.map((g) => (
              <option key={g.name} value={g.name}>{g.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* List */}
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {tab !== 'browse' && loading ? (
          <div className="flex h-full items-center justify-center text-body text-fg-tertiary">Loading…</div>
        ) : capped.length === 0 ? (
          <div className="flex h-full items-center justify-center px-4 text-center text-body text-fg-tertiary">
            {tab === 'popular'
              ? 'No sales yet — popular items will appear here.'
              : tab === 'recent'
                ? 'No recent sales yet.'
                : 'No active products in this category.'}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {capped.map((p) => (
              <QuickAddTile key={p.id} product={p} onAdd={() => onAdd(p)} />
            ))}
            {tab === 'browse' && browseProducts.length > BROWSE_CAP && (
              <div className="px-1 py-2 text-center text-label text-fg-tertiary">
                Showing first {BROWSE_CAP} — pick a category or use search to narrow.
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-body transition-colors',
        active ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function QuickAddTile({ product, onAdd }: { product: Product; onAdd: () => void }) {
  const displayStock = useLiveStock(product.id, product.currentStock);
  const tracked = product.trackInventory && !product.isService;
  const oos = tracked && displayStock <= 0;

  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={oos}
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-md border bg-bg-canvas px-2.5 py-2 text-left transition-all duration-120',
        // Unavailable, not unimportant.
        //
        // This used to be `opacity-50`, which put the product name at 3.02:1
        // and its price at 1.94:1 — effectively unreadable. WCAG exempts
        // disabled controls so it passed audit, but a customer is standing
        // there asking for this item: the cashier has to read the name to say
        // "we're out of Colgate" and read the price to offer an alternative.
        // Dimming hides the answer to a question the shop is being asked.
        //
        // So the row stays fully legible and the *state* is what is marked.
        oos
          ? 'cursor-not-allowed border-danger/25 bg-danger/[0.04]'
          : 'border-stroke-hairline/10 hover:border-accent/40 hover:shadow-e1 active:scale-[0.99]',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-body font-medium text-fg-primary">{product.name}</div>
        <div className="mt-0.5 text-label text-fg-secondary">
          <Money value={product.sellingPrice} />
          {oos && <span className="ml-1.5 font-medium text-danger-ink">· Out of stock</span>}
        </div>
      </div>
      {!oos && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent-ink transition-colors group-hover:bg-accent group-hover:text-white">
          <Plus size={14} />
        </span>
      )}
    </button>
  );
}

/**
 * Quick-add product sources for the active store. Reads recent SALE invoices
 * once, then derives:
 *   – popular: product ids ordered by how often they appear across those lines
 *   – recent:  product ids in most-recent-sold order (deduped)
 * Falls back to the store's active catalogue when there's no sales history.
 */
function useQuickAddProducts(storeId: string | null, limit = 18) {
  const [popular, setPopular] = useState<Product[]>([]);
  const [recent, setRecent] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) {
      setPopular([]);
      setRecent([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);

    (async () => {
      try {
        const invoices = await database
          .get<Invoice>('invoices')
          .query(
            Q.where('store_id', storeId),
            Q.where('is_deleted', false),
            Q.where('type', 'SALE'),
            Q.sortBy('created_at', Q.desc),
            Q.take(150),
          )
          .fetch();

        // No history → fall back to the active catalogue so the panel is useful.
        if (invoices.length === 0) {
          const catalogue = await database
            .get<Product>('products')
            .query(
              Q.where('store_id', storeId),
              Q.where('is_deleted', false),
              Q.where('is_active', true),
              Q.sortBy('name', Q.asc),
              Q.take(limit),
            )
            .fetch();
          if (alive) {
            setPopular(catalogue);
            setRecent(catalogue);
            setLoading(false);
          }
          return;
        }

        const recencyRank = new Map(invoices.map((inv, i) => [inv.id, i]));
        const lines = await database
          .get<LineItem>('line_items')
          .query(
            Q.where('invoice_id', Q.oneOf(invoices.map((i) => i.id))),
            Q.where('is_deleted', false),
          )
          .fetch();

        // Frequency + best (lowest) recency rank per product.
        const freq = new Map<string, number>();
        const bestRank = new Map<string, number>();
        for (const l of lines) {
          freq.set(l.productId, (freq.get(l.productId) ?? 0) + 1);
          const r = recencyRank.get(l.invoiceId) ?? Number.MAX_SAFE_INTEGER;
          if (r < (bestRank.get(l.productId) ?? Number.MAX_SAFE_INTEGER)) {
            bestRank.set(l.productId, r);
          }
        }

        const allIds = Array.from(freq.keys());
        const popularIds = [...allIds]
          .sort((a, b) => {
            const d = (freq.get(b) ?? 0) - (freq.get(a) ?? 0);
            return d !== 0 ? d : (bestRank.get(a) ?? 0) - (bestRank.get(b) ?? 0);
          })
          .slice(0, limit);
        const recentIds = [...allIds]
          .sort((a, b) => (bestRank.get(a) ?? 0) - (bestRank.get(b) ?? 0))
          .slice(0, limit);

        const wantedIds = Array.from(new Set([...popularIds, ...recentIds]));
        const prods = await database
          .get<Product>('products')
          .query(
            Q.where('id', Q.oneOf(wantedIds)),
            Q.where('is_deleted', false),
            Q.where('is_active', true),
          )
          .fetch();
        const byId = new Map(prods.map((p) => [p.id, p]));

        const materialize = (ids: string[]) =>
          ids.map((id) => byId.get(id)).filter((p): p is Product => !!p);

        if (alive) {
          setPopular(materialize(popularIds));
          setRecent(materialize(recentIds));
          setLoading(false);
        }
      } catch {
        if (alive) {
          setPopular([]);
          setRecent([]);
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [storeId, limit]);

  return { popular, recent, loading };
}
